import { cacheTiers } from "@/api/queryClient";
import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getMetricDefinitions } from "@/service/reqUrl.service";

import type { MetricDefinitionsResponse } from "./types";

interface UseMetricDefinitionsResult {
  data: MetricDefinitionsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY: MetricDefinitionsResponse = {
  customerSystemID: "",
  serviceLineSystemID: "",
  serviceLineName: null,
  dashboards: [],
};

/**
 * The Metrics Guide's dictionary, from `[DayLight].[GetMetricDefinitions]` —
 * every dashboard the service line reports on, its metrics, and each metric's
 * description, why it matters, formula, drill grain and related metrics.
 *
 * Definitions, not values: no date range and no filters, so the response only
 * changes when an admin edits the dictionary. Cached at bootstrap tier (30 min)
 * alongside `dashboard/config`, keyed by service line because Home Health and
 * Hospice define different metric sets.
 *
 * `CustomerSystemID`/`UserEmail` are derived server-side from the JWT; the
 * service line is the one input, so the call waits until the config has
 * resolved it.
 */
export function useMetricDefinitions(
  serviceLineSystemID: string,
): UseMetricDefinitionsResult {
  const userKey = useUserKey();

  const { data, loading, error, refetch } = useApiQuery<
    MetricDefinitionsResponse | null,
    MetricDefinitionsResponse
  >({
    queryKey: qk.metricDefinitions(userKey, serviceLineSystemID),
    url: `${getMetricDefinitions}?serviceLineSystemID=${encodeURIComponent(serviceLineSystemID)}`,
    body: null,
    method: "GET",
    enabled: !!serviceLineSystemID,
    fallbackMessage: "Failed to fetch metric definitions",
    // A 200 with an empty body means "the dictionary holds nothing for this
    // service line" — a real, renderable answer (the guide shows its empty
    // state), not a failure.
    select: (raw) => raw ?? EMPTY,
    ...cacheTiers.bootstrap,
  });

  return { data, loading, error, refetch };
}
